<template>
  <div class="admin-enterprise" :style="cssProps">
    <aside class="sidebar">
      <div class="brand-section">
        <div class="logo-wrapper">
          <img :src="proyectorConfig.logo || defaultLogo" alt="Logo" class="admin-logo">
        </div>
        <h2>U-VOTO<span>PRO</span></h2>
      </div>

      <div class="branding-controls">
        <p class="section-title">Branding Dinámico</p>
        <div class="control-card">
          <div class="control-group">
            <label>Color Maestro</label>
            <div class="color-picker-wrapper">
              <input type="color" v-model="proyectorConfig.color" @change="actualizarBranding">
              <span class="hex-value">{{ proyectorConfig.color }}</span>
            </div>
          </div>
          <div class="control-group">
            <label>Logo de Empresa</label>
            <input v-model="proyectorConfig.logo" @blur="actualizarBranding" placeholder="URL de imagen...">
          </div>
        </div>
      </div>

      <div class="questions-nav">
        <p class="section-title">Timeline de Preguntas</p>
        <button class="btn-add-new" @click="prepararNueva">
          <span class="plus-icon">+</span> Nueva Interacción
        </button>
        <ul class="q-list">
          <li v-for="p in listaPreguntas" :key="p.id" 
              :class="{ active: preguntaId === p.id }"
              @click="seleccionarPregunta(p)">
            <div class="q-info">
              <span class="q-title">{{ p.titulo }}</span>
              <span class="q-meta">{{ Object.values(p.votos || {}).reduce((a,b)=>a+b,0) }} votos</span>
            </div>
            <span class="q-status-dot" :class="p.activo ? 'live' : 'closed'"></span>
          </li>
        </ul>
      </div>
    </aside>

    <main class="main-content">
      <header class="header-status">
        <div class="info-header">
          <span class="canal-tag">CANAL ACTIVO</span>
          <h1>{{ canalId }}</h1>
        </div>
        <div class="header-actions" v-if="preguntaActual">
          <button @click="eliminarPregunta" class="btn-icon-danger" title="Eliminar">🗑</button>
          <button @click="toggleEstado" :class="preguntaActual.activo ? 'btn-status-off' : 'btn-status-on'">
            {{ preguntaActual.activo ? 'Detener Votación' : 'Activar Votación' }}
          </button>
          <button @click="proyectarPregunta" class="btn-glow">
            <span class="icon">🚀</span> PROYECTAR AHORA
          </button>
        </div>
      </header>

      <div class="workspace">
        <div v-if="tab === 'editor'" class="editor-card animate-in">
          <div class="card-header">
            <h3>Diseñar Pregunta</h3>
            <p>Define las opciones que verá tu audiencia en tiempo real.</p>
          </div>
          <div class="input-stack">
            <div class="input-group-vivid">
              <label>Pregunta Principal</label>
              <input v-model="nuevaP.titulo" class="main-input" placeholder="¿Qué opinas sobre...?">
            </div>
            <label class="label-small">Opciones de Respuesta</label>
            <div v-for="(opt, i) in nuevaP.opciones" :key="i" class="opt-input-wrapper">
              <div class="opt-decorator" :style="{background: proyectorConfig.color}">{{ i + 1 }}</div>
              <input v-model="nuevaP.opciones[i]" placeholder="Escribe una respuesta...">
            </div>
            <button v-if="nuevaP.opciones.length < 4" @click="nuevaP.opciones.push('')" class="btn-add-opt">
              + Añadir otra opción
            </button>
          </div>
          <button @click="guardarPregunta" class="btn-save-vivid">
            Guardar en Biblioteca <span>→</span>
          </button>
        </div>

        <div v-if="tab === 'proyeccion' && preguntaActual" class="projection-grid animate-in">
          <div class="qr-panel">
            <div class="qr-container">
              <div class="qr-glow" :style="{background: proyectorConfig.color}"></div>
              <div class="qr-inner">
                <vue-qr :text="urlVotacion" :size="350" :logoSrc="proyectorConfig.logo" :margin="15" colorDark="#0f172a"></vue-qr>
              </div>
            </div>
            <div class="code-badge">
              <span>PIN DE ACCESO</span>
              <div class="big-num">{{ preguntaActual.codigoNum }}</div>
            </div>
          </div>

          <div class="stats-panel">
            <div class="stats-header">
              <h2 class="q-display">{{ preguntaActual.titulo }}</h2>
              <div class="live-badge">REAL-TIME</div>
            </div>
            
            <div class="live-results">
              <div v-for="(votos, i) in preguntaActual.votos" :key="i" class="chart-row">
                <div class="bar-info">
                  <span class="opt-text">{{ preguntaActual.opciones[i] }}</span>
                  <span class="opt-pct">{{ calcularPorcentaje(votos) }}%</span>
                </div>
                <div class="bar-track">
                  <div class="bar-fill" :style="{ width: calcularPorcentaje(votos) + '%' }">
                    <div class="bar-shine"></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="demographics-grid">
              <div class="demo-card">
                <span class="icon">👨</span>
                <div class="val">{{ preguntaActual.stats?.hombre || 0 }}</div>
                <span class="lab">Hombres</span>
              </div>
              <div class="demo-card">
                <span class="icon">👩</span>
                <div class="val">{{ preguntaActual.stats?.mujer || 0 }}</div>
                <span class="lab">Mujeres</span>
              </div>
              <div class="demo-card">
                <span class="icon">👤</span>
                <div class="val">{{ preguntaActual.stats?.otro || 0 }}</div>
                <span class="lab">Otros</span>
              </div>
              <div class="demo-card total">
                <span class="icon">📊</span>
                <div class="val">{{ totalVotos }}</div>
                <span class="lab">Votos Totales</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import vueQr from 'vue-qr/src/packages/vue-qr.vue';
import { db } from '../firebase';
import { 
  collection, doc, setDoc, onSnapshot, query, 
  orderBy, addDoc, updateDoc, deleteDoc 
} from 'firebase/firestore';

export default {
  components: { vueQr },
  data() {
    return {
      // Este ID identifica de forma única este panel y a qué datos accede
      canalId: 'corporativo-1', 
      tab: 'editor',
      preguntaId: null, // ID de la pregunta seleccionada actualmente en el panel
      listaPreguntas: [],
      canalConfig: { 
        color: '#1a1a1a', 
        logo: '', 
        preguntaActivaId: null // Este campo es vital para los usuarios
      },
      nuevaP: { 
        titulo: '', 
        opciones: ['', ''], 
        formato: 'ABC', 
        permiteComentarios: false 
      },
      preguntaActual: null,
      urlVotacion: '',
      defaultLogo: 'https://cdn-icons-png.flaticon.com/512/10987/10987760.png'
    }
  },
  computed: {
    cssProps() { 
      return { '--brand-color': this.proyectorConfig.color }; 
    },
    totalVotos() {
      if (!this.preguntaActual || !this.preguntaActual.votos) return 0;
      // Sumamos los valores del objeto de votos
      return Object.values(this.preguntaActual.votos).reduce((a, b) => Number(a) + Number(b), 0);
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      // 1. Escuchar la configuración del canal (Branding y ID activo)
      onSnapshot(doc(db, "canales", this.canalId), (snap) => {
        if (snap.exists()) {
          this.canalConfig = { ...this.canalConfig, ...snap.data() };
        } else {
          // Si el canal no existe, lo creamos inicialmente
          this.actualizarBranding();
        }
      });

      // 2. Escuchar la subcolección de preguntas de ESTE canal
      const q = query(
        collection(db, `canales/${this.canalId}/preguntas`), 
        orderBy("fecha", "desc")
      );
      
      onSnapshot(q, (snap) => {
        this.listaPreguntas = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Si tenemos una pregunta seleccionada, actualizamos sus datos en tiempo real (votos)
        if (this.preguntaId) {
          const actualizada = this.listaPreguntas.find(p => p.id === this.preguntaId);
          if (actualizada) this.preguntaActual = actualizada;
        }
      });
    },

    async actualizarBranding() {
      // Guarda color y logo en la raíz del canal
      await setDoc(doc(db, "canales", this.canalId), this.canalConfig, { merge: true });
    },

    seleccionarPregunta(p) {
      this.preguntaId = p.id;
      this.preguntaActual = p;
      this.tab = 'proyeccion';
      // La URL que el QR contendrá para que el usuario entre al canal correcto
      this.urlVotacion = `${window.location.origin}/user?canal=${this.canalId}`;
    },

    async guardarPregunta() {
      if(!this.nuevaP.titulo.trim()) return;
      
      const payload = {
        titulo: this.nuevaP.titulo,
        // Limpiamos opciones vacías y limitamos a 10 (según tu requerimiento)
        opciones: this.nuevaP.opciones.filter(o => o.trim() !== '').slice(0, 10),
        votos: this.nuevaP.opciones.reduce((acc, _, i) => ({...acc, [i]: 0}), {}),
        formato: this.nuevaP.formato || 'ABC',
        permiteComentarios: this.nuevaP.permiteComentarios || false,
        activo: true,
        fecha: new Date(),
        participantes: 0
      };

      // Guardamos en la subcolección del canal
      await addDoc(collection(db, `canales/${this.canalId}/preguntas`), payload);
      this.prepararNueva();
    },

    async proyectarPregunta() {
      if (!this.preguntaId) return;
      // CAMBIO CLAVE: Actualizamos el canal para decirle a todos los usuarios 
      // qué ID de pregunta deben cargar ahora mismo.
      await updateDoc(doc(db, "canales", this.canalId), { 
        preguntaActivaId: this.preguntaId 
      });
      alert("Pregunta proyectada a los dispositivos conectados");
    },

    async toggleEstado() {
      // Pausa o reanuda la votación en la subcolección
      await updateDoc(doc(db, `canales/${this.canalId}/preguntas`, this.preguntaId), {
        activo: !this.preguntaActual.activo
      });
    },

    async eliminarPregunta() {
      if(confirm('¿Eliminar esta pregunta permanentemente?')) {
        // Si la pregunta eliminada era la activa, limpiamos el canal
        if (this.proyectorConfig.preguntaActivaId === this.preguntaId) {
          await updateDoc(doc(db, "canales", this.canalId), { preguntaActivaId: null });
        }
        await deleteDoc(doc(db, `canales/${this.canalId}/preguntas`, this.preguntaId));
        this.prepararNueva();
      }
    },

    calcularPorcentaje(v) { 
      return this.totalVotos ? ((v / this.totalVotos) * 100).toFixed(1) : 0; 
    },

    prepararNueva() { 
      this.nuevaP = { titulo: '', opciones: ['', ''], formato: 'ABC', permiteComentarios: false }; 
      this.tab = 'editor'; 
      this.preguntaId = null; 
      this.preguntaActual = null;
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;800&display=swap');

.admin-enterprise {
  display: flex;
  height: 100vh;
  background: #0f172a;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: #f8fafc;
}

/* SIDEBAR VIBRANTE */
.sidebar {
  width: 340px;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255,255,255,0.1);
  display: flex;
  flex-direction: column;
  padding: 30px;
}

.brand-section h2 { font-weight: 800; font-size: 24px; letter-spacing: -1px; }
.brand-section span { color: var(--brand-color); }
.logo-wrapper { 
  width: 60px; height: 60px; background: white; border-radius: 18px; 
  display: flex; align-items: center; justify-content: center; margin-bottom: 15px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.3);
}
.admin-logo { width: 40px; }

.control-card {
  background: rgba(255,255,255,0.05);
  border-radius: 20px;
  padding: 20px;
  margin-top: 15px;
}

.color-picker-wrapper { display: flex; align-items: center; gap: 15px; }
input[type="color"] { 
  -webkit-appearance: none; border: none; width: 40px; height: 40px; 
  border-radius: 10px; cursor: pointer; background: none; 
}
input[type="color"]::-webkit-color-swatch { border-radius: 8px; border: 2px solid rgba(255,255,255,0.2); }

/* LISTA DE PREGUNTAS VIVA */
.q-list li {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.05);
  padding: 15px;
  border-radius: 15px;
  margin-bottom: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.q-list li:hover { background: rgba(255,255,255,0.08); transform: translateX(5px); }
.q-list li.active { 
  background: var(--brand-color); 
  box-shadow: 0 10px 20px -5px var(--brand-color);
}
.q-status-dot { width: 10px; height: 10px; border-radius: 50%; }
.q-status-dot.live { background: #10b981; box-shadow: 0 0 10px #10b981; }
.q-status-dot.closed { background: #ef4444; }

/* BOTONES CON VIDA */
.btn-glow {
  background: var(--brand-color);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 14px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 0 20px rgba(var(--brand-color), 0.4);
  transition: all 0.3s;
}
.btn-glow:hover { transform: scale(1.05) translateY(-2px); box-shadow: 0 10px 25px var(--brand-color); }

.btn-status-on { background: #10b981; color: white; border: none; padding: 10px 20px; border-radius: 10px; font-weight: 600; cursor: pointer; }
.btn-status-off { background: #ef4444; color: white; border: none; padding: 10px 20px; border-radius: 10px; font-weight: 600; cursor: pointer; }

/* PROJECTION GRID PRO */
.projection-grid { display: grid; grid-template-columns: 480px 1fr; gap: 40px; }

.qr-panel { 
  background: white; padding: 50px; border-radius: 40px; 
  display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.qr-container { position: relative; z-index: 1; }
.qr-glow { 
  position: absolute; top: 0; left: 0; right: 0; bottom: 0; 
  filter: blur(60px); opacity: 0.3; z-index: -1; 
}
.qr-inner { background: white; padding: 20px; border-radius: 30px; box-shadow: 0 20px 50px rgba(0,0,0,0.1); }

.stats-panel { 
  background: #1e293b; padding: 50px; border-radius: 40px; 
  box-shadow: inset 0 2px 10px rgba(255,255,255,0.05);
}

.bar-track { background: rgba(255,255,255,0.05); height: 24px; border-radius: 12px; position: relative; overflow: hidden; }
.bar-fill { 
  height: 100%; background: var(--brand-color); 
  border-radius: 12px; position: relative;
  transition: width 1.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.bar-shine { 
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  animation: shine 2s infinite;
}

.demo-card { 
  background: rgba(255,255,255,0.03); padding: 25px; border-radius: 25px; 
  border: 1px solid rgba(255,255,255,0.05); text-align: center;
  transition: transform 0.3s;
}
.demo-card:hover { transform: translateY(-10px); background: rgba(255,255,255,0.06); }
.demo-card .val { font-size: 32px; font-weight: 800; color: var(--brand-color); }

@keyframes shine { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
@keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
</style>